import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';
import millsLogoTransparent from '../../images/mills_logo_transparent.png';
import { useAuth } from '../../context/auth';
import MillsSalesCta from './MillsSalesCta';

const MILLS_METHODOLOGY_URL = 'https://millsprice.com/methodology';

const useStyles = makeStyles((theme) => ({
  dialogHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2),
  },
  attribution: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2.5),
    rowGap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  dataBy: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    lineHeight: 1,
  },
  dataByLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.65rem',
    opacity: 0.55,
    lineHeight: 1,
    position: 'relative',
    bottom: 8,
  },
  logo: {
    display: 'block',
    width: 82,
    height: 26,
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: 2,
    opacity: 0.85,
  },
  methodologyLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    '&:hover': { color: theme.palette.text.primary },
  },
  priceRow: {
    cursor: 'pointer',
    userSelect: 'none',
    '& > td': {
      cursor: 'pointer',
    },
  },
  publicationControl: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1),
  },
}));

const formatNumber = (value, includeSign = false) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const number = Number(value);
  return `${includeSign && number > 0 ? '+' : ''}${number.toFixed(2)}`;
};

export default function RackPriceMarker({ city, publication = 'LATEST', onPublicationChange }) {
  const classes = useStyles();
  const { millsPricingAccess } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const showSalesCta = Number(millsPricingAccess) !== 1;
  const assessedAt = city.prices.reduce((latest, price) => (
    !latest || String(price.assessDate || '') > String(latest) ? price.assessDate : latest
  ), null);
  const locationLabel = city.city && city.city.includes(',')
    ? city.city
    : [city.city, city.state].filter(Boolean).join(', ');

  const closeDialog = () => {
    setSelectedPrice(null);
    setOpen(false);
  };

  const priceDetails = selectedPrice ? [
    ['City', city.city],
    ['State', city.state],
    ['Product', selectedPrice.productName || selectedPrice.specification],
    ['Product Category', selectedPrice.productCategory],
    ['Product Code', selectedPrice.specCode],
    ['Symbol', selectedPrice.symbol],
    ['Publication', selectedPrice.publication],
    ['Price (USC/gal)', selectedPrice.value],
    ['Change', selectedPrice.change],
    ['Change Percent', selectedPrice.changePercent],
    ['Assessment Date', selectedPrice.assessDate],
  ] : [];

  return (
    <div style={{ zIndex: 8 }}>
      <Tooltip title={locationLabel} placement="top" arrow>
        <IconButton
          size="small"
          aria-label={`View rack pricing for ${locationLabel}`}
          onClick={() => setOpen(true)}
          style={{ background: '#f5a623', color: '#17263c', padding: 3 }}
        >
          <AttachMoneyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <div className={classes.dialogHeader}>
            <div>
              <Typography variant="h6">{city.city} Rack Pricing</Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                MARC assessment · ¢/gal{assessedAt ? ` · ${new Date(assessedAt).toLocaleString()}` : ''}
              </Typography>
            </div>
            <div className={classes.attribution}>
              <div className={classes.dataBy}>
                <Typography variant="caption" className={classes.dataByLabel}>Data by</Typography>
                <img src={millsLogoTransparent} alt="Mills" className={classes.logo} />
              </div>
              <Link
                href={MILLS_METHODOLOGY_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                className={classes.methodologyLink}
              >
                View methodology &amp; specifications
                <OpenInNewIcon style={{ fontSize: 13, marginLeft: 3, verticalAlign: 'text-bottom' }} />
              </Link>
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 4 }}>
          <div className={classes.publicationControl}>
            <ButtonGroup size="small" color="primary" aria-label="Select Mills price publication">
              {[
                ['LATEST', 'Latest'],
                ['TR', 'Morning'],
                ['TE', 'Evening'],
              ].map(([value, label]) => (
                <Button
                  key={value}
                  variant={publication === value ? 'contained' : 'outlined'}
                  onClick={() => onPublicationChange(value)}
                  aria-pressed={publication === value}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          <Paper variant="outlined" style={{ overflowX: 'auto', marginBottom: 20 }}>
            <Table size="small" aria-label={`${city.city} rack prices`}>
              <TableHead>
                <TableRow>
                  <TableCell>Specification</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Publication</TableCell>
                  <TableCell align="right">MARC (¢/gal)</TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {city.prices.map((price) => {
                  const change = Number(price.change);
                  return (
                    <TableRow
                      key={`${price.symbol}-${price.assessDate || ''}`}
                      className={classes.priceRow}
                      hover
                      tabIndex={0}
                      onClick={() => setSelectedPrice(price)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedPrice(price);
                        }
                      }}
                    >
                      <TableCell>{price.productName || price.specification}</TableCell>
                      <TableCell>{price.symbol}</TableCell>
                      <TableCell>{price.publication}</TableCell>
                      <TableCell align="right">{formatNumber(price.value)}</TableCell>
                      <TableCell
                        align="right"
                        style={{ color: change > 0 ? '#188038' : change < 0 ? '#d93025' : 'inherit', fontWeight: 600 }}
                      >
                        {formatNumber(price.change, true)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
          {showSalesCta && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <MillsSalesCta />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(selectedPrice)}
        onClose={() => setSelectedPrice(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedPrice?.productName || selectedPrice?.specification || 'Mills Rack Price Details'}
        </DialogTitle>
        <DialogContent>
          {priceDetails.map(([label, value]) => (
            <Typography key={label} gutterBottom>
              <strong>{label}:</strong>{' '}
              {value === null || value === undefined || value === '' ? 'N/A' : String(value)}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPrice(null)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
